import Bugsnag from '@bugsnag/js'
import { doShowSnackbar } from 'actions/snackbar'
import appGraphql from 'api/app_graphql'
import * as types from 'constants/action_types'
import { oauthTokenSelector } from 'selectors/app'
import { selectCurrentTicketId } from 'selectors/tickets/current/selectCurrentTicketId'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import debug, { logError } from 'util/debug'
import { toPlainObject } from 'util/file'
import { selectDraftById } from 'ducks/drafts2/selectors'
import storage from 'util/storage'

const MAX_ATTACHMENTS = 25

function buildBugsnagError(title, error) {
  return new Error(title, {
    severity: 'error',
    metaData: {
      meta: { error },
    },
  })
}

function notify(msg, err) {
  debug(msg, err)
  Bugsnag.notify(buildBugsnagError(msg, err))
}

export function doUploadFile(
  state,
  dispatch,
  file,
  uploadData,
  forNote,
  draftId
) {
  const ticketId = selectCurrentTicketId(state)
  const plainFileObject = toPlainObject(file)
  const { key, previewUrl, request } = uploadData

  dispatch({
    type: types.ATTACHMENT_UPLOAD_STARTED,
    data: {
      ticketId,
      file: plainFileObject,
      key,
      forNote,
      expiringUrl: previewUrl,
      draftId,
    },
  })

  return new Promise((resolve, reject) => {
    const formData = new FormData()
    const xhr = new XMLHttpRequest()

    request.data.forEach(({ name, value }) => {
      if (name === 'file') {
        formData.append(name, file)
      } else {
        formData.append(name, value)
      }
    })

    xhr.upload.onprogress = e => {
      const { total, loaded } = e
      const percentage = Math.round(loaded / total * 100)

      dispatch({
        type: types.ATTACHMENT_UPLOAD_PROGRESSED,
        data: {
          draftId,
          file: plainFileObject,
          percentage,
          ticketId,
          forNote,
          expiringUrl: previewUrl,
        },
      })
    }
    xhr.onload = e => {
      /* eslint-disable eqeqeq */
      const { status } = e.target
      if (status === 200 || status === 204) {
        /* eslint-enable eqeqeq */
        const payload = {
          file: plainFileObject,
          url: request.url,
          ticketId,
          key,
          forNote,
          expiringUrl: previewUrl,
          draftId,
        }
        resolve(payload)
        dispatch({
          type: types.ATTACHMENT_UPLOAD_FINISHED,
          data: payload,
        })
      } else {
        notify('Attachment upload: API error', e)
      }
    }
    xhr.onerror = err => {
      notify('Attachment upload: network error', err)

      reject({ status: xhr.status, error: err })
      dispatch({
        type: types.ATTACHMENT_UPLOAD_FAILED,
        data: {
          draftId,
          ticketId,
          file: plainFileObject,
          key,
          forNote,
          expiringUrl: previewUrl,
          error: err,
          status: xhr.status,
        },
      })
    }

    xhr.open(request.method, request.url)
    xhr.send(formData)
  })
}

export function getUploadPayload(state, fileName) {
  return new Promise(resolve => {
    const token = oauthTokenSelector(state)
    const query = `
      mutation UploadPayloadGenerate($fileName: String) {
        uploadPayloadGenerate(input: {fileName: $fileName}) {
          key
          previewUrl
          request {
            url
            method
            contentType
            data {
              name
              value
            }
          }
        }
      }
    `
    const variables = { fileName }
    appGraphql(token, query, variables).then(res => {
      resolve(res.json.data.uploadPayloadGenerate)
    })
  })
}

export function doUploadFiles(fileList, forNote, draftId) {
  return (dispatch, getState) => {
    const state = getState()
    const draft = selectDraftById(state, draftId)

    if (
      draft?.attachments &&
      Object.keys(draft.attachments).length + fileList.length > MAX_ATTACHMENTS
    ) {
      dispatch(
        doShowSnackbar(
          `Oops. You can only add up to ${MAX_ATTACHMENTS} attachments to a single message!`
        )
      )
      return Promise.resolve()
    }

    // We need a presigned URL for each file.
    const promises = []

    for (let i = 0; i < fileList.length; i += 1) {
      const file = toPlainObject(fileList[i])
      promises.push(
        getUploadPayload(state, file.name).then(uploadData => {
          return doUploadFile(
            state,
            dispatch,
            fileList[i],
            uploadData,
            forNote,
            draftId
          )
        })
      )
    }

    return Promise.all(promises).catch(err => {
      logError(err)
      dispatch(
        doShowSnackbar(
          'Oops. There was a problem while uploading your attachments'
        )
      )
    })
  }
}

export function doDeleteFile(file, forNote, draftId) {
  return (dispatch, getState) => {
    const state = getState()
    const ticketId = selectCurrentTicketId(state)

    dispatch({
      type: types.DELETE_ATTACHMENT,
      data: {
        ticketId,
        file,
        forNote,
        draftId,
      },
    })
  }
}

export function doUploadHistory() {
  return (dispatch, getState) => {
    const state = getState()
    const date = new Date().toJSON()
    const payload = {
      actions: window.actionHistory.entries,
      state,
      storage: storage.dump(),
    }
    const contents = JSON.stringify(payload)
    const currentUser = selectCurrentUser(state)
    const filename = `errordumps/${currentUser.id}-${date}.json`
    const file = new File([contents], filename, { type: 'application/json' })
    // eslint-disable-next-line no-unused-vars
    const handler = _action => {}
    return getUploadPayload(state, filename).then(uploadData => {
      return doUploadFile(state, handler, file, uploadData, false)
    })
  }
}

window.doUploadHistory = doUploadHistory
